import { useRef, useState } from "react";
import { Link } from "react-router";
import { previewHandoffDestination, type PendingHandoff } from "../lib/preview-signin";

/**
 * #5250 round 2 (PR 5289 review, finding 1): on the staging slot, the one
 * place a sign-in is handed on to a PR preview. It names the preview host and
 * the signed-in email, and its button is the only caller of
 * POST /api/auth-preview/handoff, so a link to the slot never moves an
 * existing session onto a preview by itself.
 */
export function PreviewHandoffConfirm({ handoff, email, returnTo }: { handoff: PendingHandoff; email: string; returnTo: string }) {
  const [busy, setBusy] = useState(false);
  // A double click lands twice before the disabled state renders; the ref sees the first.
  const asked = useRef(false);

  async function open() {
    if (asked.current) return;
    asked.current = true;
    setBusy(true);
    window.location.replace(await previewHandoffDestination(handoff, returnTo));
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-sm space-y-6 p-8">
        <div className="space-y-2 text-center">
          <h1 className="text-xl font-semibold text-foreground">Open this preview?</h1>
          <p className="font-mono text-sm text-foreground break-all">{handoff.host}</p>
        </div>

        <button
          type="button"
          onClick={open}
          disabled={busy}
          aria-label={`Open ${handoff.host} as ${email}`}
          className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground break-words transition-colors hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? "Opening..." : `Continue as ${email}`}
        </button>

        <p className="text-center text-sm">
          <Link to="/" className="text-muted-foreground underline-offset-4 hover:underline">
            Cancel
          </Link>
        </p>
      </div>
    </div>
  );
}
